/**
 * Deletion State Management
 * Provides an abstraction layer for tracking experimental app deletion state.
 * MVP uses sessionStorage, can be swapped for backend persistence later.
 */

/**
 * Represents the state of an app deletion operation
 */
export interface DeletionStateEntry {
  /** Application ID being deleted */
  applicationId: number;
  /** When deletion was initiated */
  timestamp: number;
  /** Current deletion status */
  status: 'in-progress' | 'failed';
  /** Resources that failed to delete */
  failedResources?: string[];
  /** Error messages per resource */
  errors?: Record<string, string>;
}

/**
 * Interface for managing deletion state - allows swapping storage implementations
 */
export interface DeletionStateManager {
  /** Mark an app as being deleted */
  markAsDeleting(appId: number): void;
  /** Mark an app deletion as failed with specific resource errors */
  markAsFailed(
    appId: number,
    failedResources: string[],
    errors: Record<string, string>,
  ): void;
  /** Clear deletion state for an app (successful deletion or cancellation) */
  clearDeletion(appId: number): void;
  /** Get deletion state for a specific app */
  getDeletionState(appId: number): DeletionStateEntry | null;
  /** Get all apps currently in deletion state */
  getAllDeletingApps(): DeletionStateEntry[];
}

/**
 * SessionStorage-based implementation for MVP
 * Data persists across page reloads but clears on browser close
 */
class SessionDeletionStateManager implements DeletionStateManager {
  private readonly STORAGE_KEY = 'experimental-apps-deletion-state';

  /**
   * Get all deletion state from session storage
   */
  protected getState(): Record<number, DeletionStateEntry> {
    try {
      const data = sessionStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      // Silent failure - return empty state
      return {};
    }
  }

  /**
   * Save deletion state to session storage
   * @param state - The deletion state to save
   */
  protected setState(state: Record<number, DeletionStateEntry>): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Silent failure - session storage may be unavailable
    }
  }

  markAsDeleting(appId: number): void {
    const state = this.getState();
    state[appId] = {
      applicationId: appId,
      timestamp: Date.now(),
      status: 'in-progress',
    };
    this.setState(state);
  }

  markAsFailed(
    appId: number,
    failedResources: string[],
    errors: Record<string, string>,
  ): void {
    const state = this.getState();
    const existing = state[appId];

    if (existing) {
      // Merge with existing failed resources
      const allFailedResources = Array.from(
        new Set([...(existing.failedResources || []), ...failedResources]),
      );
      const allErrors = { ...(existing.errors || {}), ...errors };

      state[appId] = {
        ...existing,
        status: 'failed',
        failedResources: allFailedResources,
        errors: allErrors,
      };
      this.setState(state);
    }
  }

  clearDeletion(appId: number): void {
    const state = this.getState();
    delete state[appId];
    this.setState(state);
  }

  getDeletionState(appId: number): DeletionStateEntry | null {
    return this.getState()[appId] || null;
  }

  getAllDeletingApps(): DeletionStateEntry[] {
    return Object.values(this.getState());
  }
}

/**
 * Extended SessionDeletionStateManager with cleanup utility
 */
class SessionDeletionStateManagerWithCleanup extends SessionDeletionStateManager {
  /**
   * Clean up stale deletion states (apps deleted outside this session)
   * Call this when fetching fresh app data
   * @param existingAppIds - Array of application IDs that currently exist
   */
  cleanupStaleStates(existingAppIds: number[]): void {
    const state = this.getState();
    const stateAppIds = Object.keys(state).map(Number);

    // eslint-disable-next-line no-console
    console.log(
      '[DeletionStateManager] Cleanup - existing apps:',
      existingAppIds,
    );
    // eslint-disable-next-line no-console
    console.log(
      '[DeletionStateManager] Cleanup - current deletion states:',
      state,
    );

    // Remove deletion records for apps that no longer exist
    const staleIds = stateAppIds.filter(id => !existingAppIds.includes(id));

    // Also clear any "in-progress" states for existing apps
    // (they're stale after page reload - the deletion either completed or failed)
    const inProgressIds = stateAppIds.filter(
      id => existingAppIds.includes(id) && state[id]?.status === 'in-progress',
    );

    const idsToRemove = [...staleIds, ...inProgressIds];

    // eslint-disable-next-line no-console
    console.log('[DeletionStateManager] Cleanup - removing IDs:', idsToRemove);

    if (idsToRemove.length > 0) {
      idsToRemove.forEach(id => delete state[id]);
      this.setState(state);

      // Trigger re-render for all cards
      window.dispatchEvent(new Event('deletion-state-changed'));
    }
  }
}

/**
 * Singleton instance - can be swapped for backend implementation later
 */
export const deletionStateManager =
  new SessionDeletionStateManagerWithCleanup();
